import { SHARE_CARD_SHAPES } from '../share/appShare';
import { plantSheetFileName } from './plantSheet';

/**
 * How a plant travels: which picture, in which language, under what address, and
 * what to say about it (SPEC-019).
 *
 * The counterpart to `domain/share/appShare.js`, and deliberately its shape —
 * the path is a RULE stated here and restated for the build in
 * `scripts/share/plantCardPath.js`, bound by `plantShare.test.js`. Two owners
 * of one generated path is how a share quietly degrades to a link with nothing
 * in the diff to explain it (SPEC-005 D-7).
 *
 * Pure: no fetch, no `navigator`, no URL object. Loading the file is
 * `infrastructure/content/plantShareCardFile.js`, sharing it is
 * `application/pwa/sharePlantCard.js`, and the canonical address it is shared
 * under is `plantShareUrl` there, because that one needs a `location`.
 */

/**
 * The languages a plant has actually been rendered in.
 *
 * NOT the three the message catalogues carry, which is the difference between
 * this and the app's own card. A plant card is nine parts prose to one part
 * chrome — the common name, the tagline, the description are all
 * `prose.<lang>.json` — so the set of languages a plant can travel in is exactly
 * the set of prose files that exist. `prose.de.json` does not, and a German
 * reader already sees English prose on screen; a German-chrome card around
 * English prose would be a plant in two languages at once (D-1).
 *
 * `plantShare.test.js` binds this to what is actually on disk, so adding
 * `prose.de.json` without re-rendering fails rather than 404s.
 */
export const PLANT_ARTWORK_LANGUAGES = ['en', 'hu'];

const FALLBACK_LANGUAGE = 'en';

/**
 * English for anything unrendered, matching `fetchProse`, which already answers
 * the same way for the same input. A 404 here would cost the picture on a share
 * that had no other reason to fail.
 */
export function plantArtworkLanguage(languageCode) {
    return PLANT_ARTWORK_LANGUAGES.includes(languageCode) ? languageCode : FALLBACK_LANGUAGE;
}

/**
 * The app's two shapes, plus the one only a plant has (SPEC-019 D-2, extended).
 *
 * `unfurl` and `portrait` are SPEC-020's and are imported rather than restated,
 * because a plant is not a different problem: 1.91:1 is what every unfurling
 * platform crops to, 4:5 is the tallest a story or a message bubble renders
 * whole.
 *
 * `landscape` is 1.91:1 AGAIN, and the duplication is the point. `unfurl` is
 * fetched by a crawler and deliberately says less than it could — the platform
 * prints `og:description` as text beside it, so a tagline inside the picture is
 * the same sentence twice. A Facebook post is the opposite case: the picture is
 * UPLOADED, it carries whatever caption the poster types and nothing else, and
 * 1.91:1 is the widest the feed renders before it crops. So the post card is the
 * wide composition that DOES carry the tagline, at the resolution a feed on a
 * high-density phone actually asks for — 1920 wide, where the crawler's card is
 * held to 1200 by a link preview's patience.
 *
 * 1920x1008 rather than 1920x1005: the same 1200:630 ratio as the unfurl, so one
 * composition scales into both, and both dimensions stay whole MCU blocks.
 *
 * This lives HERE rather than in `domain/share/appShare.js` because the app's own
 * invitation has no Facebook option to render one for, and a shape nothing hands
 * over is a file nobody asked for.
 */
export const PLANT_CARD_SHAPES = {
    ...SHARE_CARD_SHAPES,
    landscape: { key: 'landscape', width: 1920, height: 1008 },
};

/**
 * Same-origin, so the service worker can cache it and no CORS is involved.
 *
 * Under `assets/share/` rather than `assets/content/plants/`: the content
 * ingest owns every file in that tree by name (`derivedOutput.js`) and deletes
 * the ones it does not recognise, which is exactly the trap SPEC-005 D-7 fell
 * into once already.
 */
export function plantCardPath(plantId, languageCode, shapeKey = 'portrait') {
    const shape = PLANT_CARD_SHAPES[shapeKey];

    if (!shape) {
        throw new Error(`No share card shape called ${shapeKey}`);
    }

    return `/assets/share/plants/${plantId}`
        + `/card-${plantArtworkLanguage(languageCode)}-${shape.width}x${shape.height}.jpg`;
}

/**
 * The card lands in the same Photos roll as the sheet and answers to the same
 * name: the plant's, not the file's. A recipient reading
 * `card-hu-1080x1350.jpg` learns nothing.
 */
export const plantPictureFileName = plantSheetFileName;

/**
 * What the share sheet says about the plant, in the language on screen.
 *
 * One message key, not three. A plant's name joined to its own tagline by an em
 * dash is composition rather than copy — the call `plantProperties.js` already
 * makes about size ranges — and a key whose whole value is `{plant} — {tagline}`
 * would be identical in all three catalogues and trip the untranslated check in
 * `locales.test.js` for a legitimate reason. What IS copy is the invitation, and
 * that is the key.
 *
 * `text` deliberately does not carry the address. `shareImage` appends it —
 * `${text} — ${url}` — because iOS collapses a mixed `files` + `url` payload
 * back into a link share (SPEC-005 D-6). So the invitation ends on a phrase that
 * reads correctly with an address after it rather than on a colon.
 */
export function plantShareMessage({ commonName, tagline }, t) {
    const name = String(commonName ?? '').trim();
    const line = String(tagline ?? '').trim();

    return {
        title: name,
        text: `${line ? `${name} — ${line}` : name}\n\n${t('plants.share.invitation')}`,
    };
}
